import React, { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { CAPABILITY_LABELS } from "@/components/brand/CapabilityBadges";

const RUNS = [["local_stdio", "Local (stdio)"], ["remote", "Remote URL"], ["both", "Both"], ["unknown", "Unknown"]];
const csv = (xs) => (xs || []).join(", ");
const uncsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

// Public server profile for this item: draft (Claude writes only "what it does"), edit, approve, unpublish.
// "If compromised" / "To reduce risk" and the status line come from fixed templates (preview below).
export default function ProfilePanel({ itemId, itemType, styles }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.itemProfile(itemId).then((r) => {
    setData(r.data);
    const f = r.data.profile?.fields;
    setForm(f ? { what_it_does: f.what_it_does || "", runs_where: f.runs_where || "unknown", capabilities: f.capabilities || [],
                  secrets_needed: csv(f.secrets_needed), contacted_domains: csv(f.contacted_domains) } : null);
  }).catch((ex) => setMsg("Error: " + ex.message)), [itemId]);
  useEffect(() => { load(); }, [load]);
  if (itemType !== "mcp_server") return null;
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); setMsg(ok); await load(); } catch (ex) { setMsg("Error: " + ex.message); } finally { setBusy(false); }
  };
  const p = data?.profile;
  const toggleCap = (c) => setForm({ ...form, capabilities: form.capabilities.includes(c) ? form.capabilities.filter((x) => x !== c) : [...form.capabilities, c] });
  return (
    <div style={styles.card} data-testid="profile-panel">
      <h3 style={{ marginTop: 0 }}>Public profile</h3>
      {!p ? (
        <>
          <p style={{ color: "var(--hs-text-muted)", marginTop: 0 }}>No profile yet. Claude drafts the description; everything else is computed from our data. Nothing is public until you publish it.</p>
          <button type="button" data-testid="profile-create" style={styles.btn} disabled={busy}
                  onClick={() => act(() => api.createProfile(itemId), "Draft created.")}>Create profile</button>
        </>
      ) : (
        <>
          <div style={{ ...styles.row }}>
            <span className={`hs-badge ${p.state === "published" ? "hs-badge-safe" : ""}`} data-testid="profile-state">{p.state}</span>
            <span data-testid="profile-status">{data.preview.status_text}</span>
            {data.blocked && <span className="hs-badge hs-badge-danger" data-testid="profile-blocked">cannot publish: {data.blocked}</span>}
            <span style={{ fontSize: 12, color: "var(--hs-text-muted)" }}>/servers/{p.slug} · drafted by {p.draft_meta?.model || "—"}</span>
          </div>
          <div style={{ display: "grid", gap: 10, maxWidth: 760 }}>
            <div>
              <label className="hs-label" htmlFor="pf-what">What it does (1–2 sentences, no links, no safety claims)</label>
              <textarea id="pf-what" data-testid="profile-what" rows={3} style={{ ...styles.input, width: "100%" }}
                        value={form.what_it_does} onChange={(e) => setForm({ ...form, what_it_does: e.target.value })} />
            </div>
            <div>
              <label className="hs-label" htmlFor="pf-runs">Runs where</label>
              <select id="pf-runs" data-testid="profile-runs" style={styles.select} value={form.runs_where} onChange={(e) => setForm({ ...form, runs_where: e.target.value })}>
                {RUNS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <fieldset style={{ border: "1px solid var(--hs-border)", borderRadius: 10, padding: 8 }}>
              <legend className="hs-label">Capabilities (risk text comes from the fixed templates)</legend>
              {Object.entries(CAPABILITY_LABELS).map(([c, l]) => (
                <label key={c} style={{ marginRight: 14, display: "inline-flex", gap: 6, alignItems: "center", minHeight: 44 }}>
                  <input type="checkbox" data-testid={`profile-cap-${c}`} checked={form.capabilities.includes(c)} onChange={() => toggleCap(c)} />{l}
                </label>))}
            </fieldset>
            <div>
              <label className="hs-label" htmlFor="pf-secrets">Secrets needed (names only, comma separated)</label>
              <input id="pf-secrets" data-testid="profile-secrets" style={{ ...styles.input, width: "100%" }} value={form.secrets_needed}
                     onChange={(e) => setForm({ ...form, secrets_needed: e.target.value })} />
            </div>
            <div>
              <label className="hs-label" htmlFor="pf-domains">Contacted domains (comma separated)</label>
              <input id="pf-domains" data-testid="profile-domains" style={{ ...styles.input, width: "100%" }} value={form.contacted_domains}
                     onChange={(e) => setForm({ ...form, contacted_domains: e.target.value })} />
            </div>
          </div>
          <div style={{ ...styles.row, marginTop: 12 }}>
            <button type="button" data-testid="profile-save" style={styles.btnDim} disabled={busy}
                    onClick={() => act(() => api.editProfile(p.id, { what_it_does: form.what_it_does, runs_where: form.runs_where,
                      capabilities: form.capabilities, secrets_needed: uncsv(form.secrets_needed), contacted_domains: uncsv(form.contacted_domains) }), "Saved.")}>Save changes</button>
            <button type="button" data-testid="profile-redraft" style={styles.btnDim} disabled={busy}
                    onClick={() => { if (window.confirm("Re-draft from the current data? Your edits to the fields are replaced.")) act(() => api.createProfile(itemId), "Re-drafted."); }}>Re-draft</button>
            {p.state !== "published" ? (
              <button type="button" data-testid="profile-publish" style={styles.btn} disabled={busy || !!data.blocked}
                      onClick={() => { if (window.confirm("Publish this profile at /servers/" + p.slug + "?")) act(() => api.publishProfile(p.id), "Published."); }}>Publish profile</button>
            ) : (
              <>
                <button type="button" data-testid="profile-unpublish" style={styles.btnDim} disabled={busy}
                        onClick={() => act(() => api.unpublishProfile(p.id), "Unpublished.")}>Unpublish</button>
                <a className="hs-link" href={`/servers/${p.slug}`}>View public page</a>
              </>
            )}
          </div>
          <details style={{ marginTop: 12 }} data-testid="profile-preview">
            <summary style={{ cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center" }}>Preview: fixed risk text and status</summary>
            <div style={{ fontSize: 14 }}>
              <div><strong>{data.preview.status_text}</strong> · {data.preview.disclaimer}</div>
              <ul>{data.preview.risks.map((r) => <li key={r.capability}><strong>{r.label}:</strong> {r.if_compromised} <em>{r.to_reduce_risk}</em></li>)}</ul>
            </div>
          </details>
        </>
      )}
      {msg && <div style={{ fontSize: 12, color: "var(--hs-text-muted)", marginTop: 6 }} data-testid="profile-msg">{msg}</div>}
    </div>
  );
}
